import { useEffect, useMemo, useState } from "react";
import { theme, card, emptyFrame, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { api, apiBlob, apiErrorMessage } from "../api/client";
import { safeHref } from "../utils/safeHref";
import { StandardsRefreshReview, type RefreshStatus } from "./StandardsRefreshReview";

// Platform Admin → Standards Watch. The owner's view of the standards &
// regulation watch (docs/STANDARDS-WATCH.md): the events the worker raised
// (acknowledge / mark applied / dismiss), and the catalogue — every standard
// and law the platform encodes, the edition it encodes, whether and how it is
// watched, and what each source said last time it was read.

type EventKind = "version" | "amendment" | "corrigendum" | "consultation" | "guidance" | "noise" | "unclassified";
type EventStatus = "open" | "acknowledged" | "applied" | "dismissed";
type Coverage = "watched" | "covered" | "unwatched";

interface WatchSourceView {
  id: string;
  kind: "page" | "rss" | "cellar";
  name: string;
  url: string;
  fetchedAt: string | null;
  changedAt: string | null;
  lastStatus: number | null;
  lastError: string | null;
  note: string | null;
}

interface WatchEntryView {
  key: string;
  label: string;
  category: "standard" | "law";
  frameworkIds: string[];
  regulations: string[];
  countries: string[];
  implemented: { version: string; asOf?: string; note?: string; behind?: Array<{ what: string; url: string; note?: string }> };
  affects: string[];
  reference: string | null;
  coveredBy: string | null;
  unwatchedReason: string | null;
  coverage: Coverage;
  openEvents: number;
  sources: WatchSourceView[];
  /** Whether the platform may fetch and store the official text (catalogue fullText). */
  fullText: { mode: "open"; licence: string; noiseFree: boolean } | { mode: "flag_only"; reason: string };
  autoApply: boolean;
  appliedText: { version: string; appliedAt: string; charCount: number; autoApplied: boolean } | null;
  /** Rows a refresh changed, by origin: updated / added / flagged. */
  overrides: Record<string, number>;
}

interface RefreshView {
  id: string;
  entryKey: string;
  entryLabel: string;
  eventId: string | null;
  status: RefreshStatus;
  trigger: string;
  charCount: number | null;
  suggestedVersion: string | null;
  error: string | null;
  convertedAt: string | null;
  decidedAt: string | null;
  autoApplied: boolean;
  createdAt: string;
}

interface WatchEventView {
  id: string;
  entryKey: string;
  entryLabel: string;
  sourceId: string;
  kind: EventKind;
  title: string;
  summary: string;
  effectiveDate: string | null;
  sourceUrl: string;
  evidence: string | null;
  confidence: number | null;
  status: EventStatus;
  notifiedAt: string | null;
  acknowledgedAt: string | null;
  appliedAt: string | null;
  appliedVersion: string | null;
  note: string | null;
  detectedAt: string;
}

interface WatchRunView {
  id: string;
  triggeredBy: string;
  startedAt: string;
  finishedAt: string | null;
  sourcesChecked: number;
  sourcesChanged: number;
  sourcesFailed: number;
  eventsCreated: number;
  materialEvents: number;
  notified: number;
  error: string | null;
}

interface WatchData {
  entries: WatchEntryView[];
  events: WatchEventView[];
  refreshes: RefreshView[];
  runs: WatchRunView[];
  coverage: Record<Coverage, number>;
  lastRun: WatchRunView | null;
}

const KIND_LABEL: Record<EventKind, string> = {
  version: "New version",
  amendment: "Amendment",
  corrigendum: "Corrigendum",
  consultation: "Consultation",
  guidance: "Guidance",
  noise: "Noise",
  unclassified: "Unclassified",
};

const KIND_COLOR: Record<EventKind, { color: string; bg: string }> = {
  version:      { color: theme.color.dangerText, bg: theme.color.dangerBg },
  amendment:    { color: theme.color.dangerText, bg: theme.color.dangerBg },
  corrigendum:  { color: theme.color.warningText, bg: theme.color.warningBg },
  consultation: { color: theme.color.warningText, bg: theme.color.warningBg },
  guidance:     { color: theme.color.accentText, bg: theme.color.accentBg },
  noise:        { color: theme.color.muted, bg: theme.color.panelAlt },
  unclassified: { color: theme.color.warningText, bg: theme.color.warningBg },
};

const STATUS_LABEL: Record<EventStatus, string> = { open: "Open", acknowledged: "Acknowledged", applied: "Applied", dismissed: "Dismissed" };

function fmtDate(iso: string | null | undefined, withTime = false): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return withTime
    ? d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function Pill({ label, color, bg, title }: { label: string; color: string; bg: string; title?: string }) {
  return (
    <span title={title} style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 3, color, background: bg, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function ExternalLink({ href, children }: { href: string | null; children: React.ReactNode }) {
  const safe = safeHref(href);
  if (!safe) return <span style={{ color: theme.color.muted }}>{children}</span>;
  return <a href={safe} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.accentText, textDecoration: "none", wordBreak: "break-all" }}>{children}</a>;
}

// ── Refreshing the text ───────────────────────────────────────────────────

const REFRESHABLE_KINDS: ReadonlySet<EventKind> = new Set(["version", "amendment", "corrigendum", "unclassified"]);

interface RefreshActions {
  onReview: (refreshId: string) => void;
  onConvert: (entryKey: string, eventId?: string) => Promise<void>;
}

/** Where an instrument's official text stands: flag-only, converting, ready to review, failed … */
function RefreshLine({ entry, refresh, eventId, actions }: { entry: WatchEntryView; refresh: RefreshView | null; eventId?: string; actions: RefreshActions }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (entry.fullText.mode === "flag_only") {
    return <div style={{ marginTop: 8, fontSize: 12, color: theme.color.muted }} title={entry.fullText.reason}>Flag only: the platform does not fetch this text. {entry.fullText.reason}</div>;
  }
  const convert = async () => {
    setBusy(true);
    setError("");
    try {
      await actions.onConvert(entry.key, eventId);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not queue the conversion."));
    } finally {
      setBusy(false);
    }
  };
  const small = { padding: "5px 10px", fontSize: 12 } as const;
  let body: React.ReactNode;
  if (!refresh) {
    body = <button disabled={busy} onClick={convert} style={{ ...btnSecondary, ...small }}>{busy ? "Queuing…" : "Convert the official text to markdown"}</button>;
  } else if (refresh.status === "converting") {
    body = <span style={{ color: theme.color.accentText }}>Converting the official text to markdown (queued {fmtDate(refresh.createdAt, true)})…</span>;
  } else if (refresh.status === "ready") {
    body = <button onClick={() => actions.onReview(refresh.id)} style={{ ...btnPrimary, ...small }}>Converted to markdown — review</button>;
  } else if (refresh.status === "failed") {
    body = <><span style={{ color: theme.color.dangerText }}>Conversion failed: {refresh.error ?? "unknown error"}</span> <button disabled={busy} onClick={convert} style={{ ...btnSecondary, ...small }}>Try again</button></>;
  } else if (refresh.status === "unchanged") {
    body = <span style={{ color: theme.color.muted }}>The official text is the same as the text in force ({fmtDate(refresh.convertedAt)}).</span>;
  } else if (refresh.status === "applied") {
    body = <><span style={{ color: theme.color.successText }}>Refresh {refresh.autoApplied ? "auto-applied" : "applied"} {fmtDate(refresh.decidedAt)}</span> <button onClick={() => actions.onReview(refresh.id)} style={{ background: "none", border: "none", padding: 0, color: theme.color.accentText, cursor: "pointer", fontSize: 12 }}>What was applied</button></>;
  } else {
    body = <><span style={{ color: theme.color.muted }}>Conversion {refresh.status} {fmtDate(refresh.decidedAt ?? refresh.convertedAt)}</span> <button disabled={busy} onClick={convert} style={{ ...btnSecondary, ...small }}>Convert again</button></>;
  }
  return (
    <div style={{ marginTop: 10, fontSize: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      {body}
      {error && <span style={{ color: theme.color.dangerText }}>{error}</span>}
    </div>
  );
}

function FullTextPanel({ entry, refresh, actions, onAutoApply, onWithdraw }: {
  entry: WatchEntryView;
  refresh: RefreshView | null;
  actions: RefreshActions;
  onAutoApply: (key: string, enabled: boolean) => Promise<void>;
  onWithdraw: (key: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [error, setError] = useState("");
  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(apiErrorMessage(e, fallback));
    } finally {
      setBusy(false);
      setConfirmWithdraw(false);
    }
  };
  const downloadText = () => run(async () => {
    const blob = await apiBlob(`/admin/standards-watch/entries/${encodeURIComponent(entry.key)}/text`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${entry.key}-in-force.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, "Could not download the text.");
  const ft = entry.fullText;
  const overrides = Object.entries(entry.overrides).filter(([, n]) => n > 0);
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Official text</div>
      {ft.mode === "flag_only" ? (
        <div style={{ color: theme.color.muted }}>Flag only. {ft.reason}</div>
      ) : (
        <>
          <div style={{ color: theme.color.muted }}>May be fetched and stored: {ft.licence}</div>
          {entry.appliedText ? (
            <div style={{ marginTop: 4 }}>
              In force: {entry.appliedText.version} — {entry.appliedText.autoApplied ? "auto-applied" : "applied"} {fmtDate(entry.appliedText.appliedAt)}, {entry.appliedText.charCount.toLocaleString("en-GB")} characters.{" "}
              <button disabled={busy} onClick={downloadText} style={{ background: "none", border: "none", padding: 0, color: theme.color.accentText, cursor: "pointer", fontSize: 12 }}>Download</button>
            </div>
          ) : (
            <div style={{ marginTop: 4, color: theme.color.muted }}>No official text applied yet: the compliance check and policy generation read the seeded summaries.</div>
          )}
          {overrides.length > 0 && <div style={{ marginTop: 4, color: theme.color.muted }}>Seeded rows changed by a refresh: {overrides.map(([origin, n]) => `${n} ${origin}`).join(", ")}</div>}
          <RefreshLine entry={entry} refresh={refresh} actions={actions} />
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
            {ft.noiseFree ? (
              <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }} title="Applies a converted text without review when it looks like a small move of the one in force. Never applies a change to the seeded rows.">
                <input type="checkbox" disabled={busy} checked={entry.autoApply} onChange={(e) => run(() => onAutoApply(entry.key, e.target.checked), "Could not change auto-apply.")} />
                Auto-apply the text (no row changes)
              </label>
            ) : (
              <span style={{ color: theme.color.faint }}>Auto-apply is not offered: this instrument's sources are not marked noise-free.</span>
            )}
            {entry.appliedText && !confirmWithdraw && (
              <button disabled={busy} onClick={() => setConfirmWithdraw(true)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}>Withdraw the text…</button>
            )}
            {confirmWithdraw && (
              <span>
                Back to the seeded summaries, and every row a refresh updated restored?{" "}
                <button disabled={busy} onClick={() => run(() => onWithdraw(entry.key), "Could not withdraw the text.")} style={{ ...btnPrimary, padding: "4px 10px", fontSize: 12 }}>Withdraw</button>{" "}
                <button disabled={busy} onClick={() => setConfirmWithdraw(false)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}>Cancel</button>
              </span>
            )}
          </div>
        </>
      )}
      {error && <div style={{ color: theme.color.dangerText, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

// ── Events ────────────────────────────────────────────────────────────────

function EventCard({ event, onChange, entry, refresh, actions }: {
  event: WatchEventView;
  onChange: (id: string, body: { status: EventStatus; note?: string; appliedVersion?: string }) => Promise<void>;
  entry: WatchEntryView | undefined;
  refresh: RefreshView | null;
  actions: RefreshActions;
}) {
  const [busy, setBusy] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [applying, setApplying] = useState(false);
  const [appliedVersion, setAppliedVersion] = useState("");
  const [note, setNote] = useState(event.note ?? "");
  const [error, setError] = useState("");
  const kind = KIND_COLOR[event.kind] ?? KIND_COLOR.unclassified;

  async function act(body: { status: EventStatus; note?: string; appliedVersion?: string }) {
    setBusy(true);
    setError("");
    try {
      await onChange(event.id, body);
      setApplying(false);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not update the event."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ ...card, padding: "16px 20px", opacity: event.status === "dismissed" ? 0.7 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            <Pill label={KIND_LABEL[event.kind] ?? event.kind} color={kind.color} bg={kind.bg} />
            {event.status !== "open" && <Pill label={STATUS_LABEL[event.status]} color={theme.color.muted} bg={theme.color.panelAlt} />}
            <span style={{ fontSize: 12, color: theme.color.muted }}>{event.entryLabel}</span>
            <span style={{ fontSize: 12, color: theme.color.faint }}>· detected {fmtDate(event.detectedAt, true)}</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>{event.title}</div>
          <div style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{event.summary}</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8, fontSize: 12, color: theme.color.muted }}>
            {event.effectiveDate && <span>Effective {fmtDate(event.effectiveDate)}</span>}
            {event.confidence !== null && <span>Confidence {Math.round(event.confidence * 100)}%</span>}
            <ExternalLink href={event.sourceUrl}>Open source</ExternalLink>
            {event.evidence && (
              <button onClick={() => setShowEvidence((v) => !v)} style={{ background: "none", border: "none", padding: 0, color: theme.color.accentText, cursor: "pointer", fontSize: 12 }}>
                {showEvidence ? "Hide what changed" : "Show what changed"}
              </button>
            )}
          </div>
          {showEvidence && event.evidence && (
            <pre style={{ marginTop: 10, padding: 12, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm, fontSize: 11.5, lineHeight: 1.5, color: theme.color.text, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 320, overflow: "auto" }}>
              {event.evidence}
            </pre>
          )}
          {event.appliedVersion && (
            event.status === "applied"
              ? <div style={{ marginTop: 8, fontSize: 12, color: theme.color.successText }}>Applied {fmtDate(event.appliedAt)} — platform now encodes: {event.appliedVersion}</div>
              // Reopening keeps the earlier application on record (the audit log has the reopen).
              : <div style={{ marginTop: 8, fontSize: 12, color: theme.color.muted }}>Previously applied {fmtDate(event.appliedAt)} as {event.appliedVersion}; reopened since</div>
          )}
          {event.note && !applying && <div style={{ marginTop: 6, fontSize: 12, color: theme.color.muted }}>Note: {event.note}</div>}
          {entry && (refresh || (REFRESHABLE_KINDS.has(event.kind) && event.status !== "applied" && event.status !== "dismissed")) && (
            <RefreshLine entry={entry} refresh={refresh} eventId={event.id} actions={actions} />
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch", minWidth: 170 }}>
          {event.status === "open" && (
            <button disabled={busy} onClick={() => act({ status: "acknowledged" })} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }}>Acknowledge</button>
          )}
          {event.status !== "applied" && event.status !== "dismissed" && (
            <button disabled={busy} onClick={() => setApplying((v) => !v)} style={{ ...btnPrimary, padding: "6px 12px", fontSize: 12 }}>Mark applied…</button>
          )}
          {event.status !== "dismissed" && event.status !== "applied" && (
            <button disabled={busy} onClick={() => act({ status: "dismissed", note: note || undefined })} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }}>Dismiss</button>
          )}
          {(event.status === "dismissed" || event.status === "applied" || event.status === "acknowledged") && (
            <button disabled={busy} onClick={() => act({ status: "open" })} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }}>Reopen</button>
          )}
        </div>
      </div>

      {applying && (
        <div style={{ marginTop: 12, padding: 12, background: theme.color.panelAlt, borderRadius: theme.radius.sm, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: 2, minWidth: 240 }}>
            <label style={{ fontSize: 11, color: theme.color.muted, display: "block", marginBottom: 4 }}>What the platform now encodes (required)</label>
            <input value={appliedVersion} onChange={(e) => setAppliedVersion(e.target.value)} placeholder="e.g. ISO/IEC 27001:2022 + Amd 1:2024" style={inputStyle} maxLength={300} />
          </div>
          <div style={{ flex: 2, minWidth: 240 }}>
            <label style={{ fontSize: 11, color: theme.color.muted, display: "block", marginBottom: 4 }}>Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Seed / reference text updated in …" style={inputStyle} maxLength={2000} />
          </div>
          <button disabled={busy || !appliedVersion.trim()} onClick={() => act({ status: "applied", appliedVersion: appliedVersion.trim(), note: note || undefined })} style={{ ...btnPrimary, padding: "8px 14px", fontSize: 12 }}>Save</button>
          <button disabled={busy} onClick={() => setApplying(false)} style={{ ...btnSecondary, padding: "8px 14px", fontSize: 12 }}>Cancel</button>
        </div>
      )}
      {error && <div style={{ marginTop: 8, fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
    </div>
  );
}

// ── Catalogue ─────────────────────────────────────────────────────────────

function sourceHealth(s: WatchSourceView): { label: string; color: string; bg: string; title: string } {
  if (s.lastError) return { label: "Unreachable", color: theme.color.dangerText, bg: theme.color.dangerBg, title: `${s.lastStatus ? `HTTP ${s.lastStatus}: ` : ""}${s.lastError}` };
  if (!s.fetchedAt) return { label: "Not yet read", color: theme.color.muted, bg: theme.color.panelAlt, title: "Read on the next run" };
  if (s.note) return { label: "Read (note)", color: theme.color.warningText, bg: theme.color.warningBg, title: s.note };
  return { label: "Read", color: theme.color.successText, bg: theme.color.successBg, title: `Last read ${fmtDate(s.fetchedAt, true)}` };
}

function EntryRow({ entry, refresh, actions, onAutoApply, onWithdraw }: {
  entry: WatchEntryView;
  refresh: RefreshView | null;
  actions: RefreshActions;
  onAutoApply: (key: string, enabled: boolean) => Promise<void>;
  onWithdraw: (key: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const coverage: Record<Coverage, { label: string; color: string; bg: string }> = {
    watched:   { label: "Watched", color: theme.color.successText, bg: theme.color.successBg },
    covered:   { label: `Via ${entry.coveredBy ?? "another entry"}`, color: theme.color.accentText, bg: theme.color.accentBg },
    unwatched: { label: "Not watched", color: theme.color.warningText, bg: theme.color.warningBg },
  };
  const c = coverage[entry.coverage];
  const failing = entry.sources.filter((s) => s.lastError).length;
  const lastRead = entry.sources.map((s) => s.fetchedAt).filter(Boolean).sort().pop() ?? null;

  return (
    <div style={{ borderBottom: `1px solid ${theme.color.border}` }}>
      <div onClick={() => setOpen((v) => !v)} style={{ display: "grid", gridTemplateColumns: "minmax(220px, 2fr) minmax(220px, 3fr) 120px 110px 80px", gap: 12, alignItems: "center", padding: "10px 12px", cursor: "pointer", background: open ? theme.color.panelAlt : "transparent" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{entry.label}</div>
          <div style={{ fontSize: 11, color: theme.color.faint }}>{entry.category === "standard" ? "Standard" : "Law"}{entry.countries.length > 0 && entry.countries.length <= 3 ? ` · ${entry.countries.join(", ")}` : entry.countries.length > 3 ? ` · ${entry.countries.length} countries` : ""}</div>
        </div>
        <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 1.4 }}>{entry.implemented.version}</div>
        <div><Pill label={c.label} color={c.color} bg={c.bg} /></div>
        <div style={{ fontSize: 11, color: failing ? theme.color.dangerText : theme.color.muted }}>
          {entry.coverage === "watched" ? (failing ? `${failing} of ${entry.sources.length} failing` : lastRead ? fmtDate(lastRead) : "not yet read") : "—"}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: entry.openEvents ? theme.color.dangerText : theme.color.faint, textAlign: "right" }}>{entry.openEvents || ""}</div>
      </div>
      {open && (
        <div style={{ padding: "6px 12px 16px 24px", fontSize: 12, color: theme.color.text, lineHeight: 1.55 }}>
          {entry.implemented.note && <div style={{ marginBottom: 8, color: theme.color.warningText }}>{entry.implemented.note}</div>}
          {(entry.implemented.behind?.length ?? 0) > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Newer editions</div>
              {entry.implemented.behind!.map((b) => (
                <div key={b.url} style={{ marginBottom: 3 }}>
                  <ExternalLink href={b.url}>{b.what}</ExternalLink>
                  {b.note && <span style={{ color: theme.color.muted }}> — {b.note}</span>}
                </div>
              ))}
            </div>
          )}
          {entry.unwatchedReason && <div style={{ marginBottom: 8, color: theme.color.muted }}>{entry.unwatchedReason}</div>}
          {entry.sources.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Sources</div>
              {entry.sources.map((s) => {
                const h = sourceHealth(s);
                return (
                  <div key={s.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 4, flexWrap: "wrap" }}>
                    <Pill label={h.label} color={h.color} bg={h.bg} title={h.title} />
                    <span style={{ color: theme.color.faint, fontSize: 11, textTransform: "uppercase" }}>{s.kind}</span>
                    <span>{s.name}</span>
                    <ExternalLink href={s.url}>{s.url}</ExternalLink>
                    {s.changedAt && <span style={{ color: theme.color.faint }}>last moved {fmtDate(s.changedAt)}</span>}
                    {s.lastError && <span style={{ color: theme.color.dangerText }}>{s.lastStatus ? `HTTP ${s.lastStatus} — ` : ""}{s.lastError}</span>}
                    {s.note && !s.lastError && <span style={{ color: theme.color.warningText }}>{s.note}</span>}
                  </div>
                );
              })}
            </div>
          )}
          {entry.reference && <div style={{ marginBottom: 8 }}>Reference: <ExternalLink href={entry.reference}>{entry.reference}</ExternalLink></div>}
          {(entry.frameworkIds.length > 0 || entry.regulations.length > 0) && (
            <div style={{ marginBottom: 8, color: theme.color.muted }}>
              Speaks for: {[...entry.frameworkIds, ...entry.regulations].join(" · ")}
            </div>
          )}
          <div style={{ color: theme.color.muted, marginBottom: 8 }}>Affects: {entry.affects.join(" · ")}</div>
          <FullTextPanel entry={entry} refresh={refresh} actions={actions} onAutoApply={onAutoApply} onWithdraw={onWithdraw} />
        </div>
      )}
    </div>
  );
}

// ── Tab ───────────────────────────────────────────────────────────────────

export function StandardsWatchTab() {
  const [data, setData] = useState<WatchData | null>(null);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const [runMessage, setRunMessage] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState("");
  const [eventFilter, setEventFilter] = useState<"open" | "all" | "noise">("open");
  const [entryFilter, setEntryFilter] = useState<"all" | "standard" | "law" | "unwatched" | "failing">("all");
  const [search, setSearch] = useState("");
  const [reviewId, setReviewId] = useState<string | null>(null);

  function load() {
    api.get<WatchData>("/admin/standards-watch")
      .then(setData)
      .catch((e) => setError(apiErrorMessage(e, "Failed to load the standards watch.")));
  }
  useEffect(load, []);

  async function runNow() {
    setRunning(true);
    setRunMessage("");
    try {
      const res = await api.post<{ queued: boolean }>("/admin/standards-watch/run");
      setRunMessage(res.queued ? "A pass has been queued; results appear here as the worker reads each source (a few minutes)." : "A pass is already running or waiting.");
    } catch (e) {
      setRunMessage(apiErrorMessage(e, "Could not queue a run."));
    } finally {
      setRunning(false);
    }
  }

  async function downloadReport() {
    setDownloading(true);
    setDownloadMessage("");
    try {
      const blob = await apiBlob("/admin/standards-watch/report");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `standards-watch-${new Date().toISOString().slice(0, 10)}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadMessage(apiErrorMessage(e, "Could not build the report."));
    } finally {
      setDownloading(false);
    }
  }

  // Applying a new edition or an amendment publishes it to the organisations it affects (backend services/regulatoryWatch.ts).
  const [publishMessage, setPublishMessage] = useState("");
  async function updateEvent(id: string, body: { status: EventStatus; note?: string; appliedVersion?: string }) {
    const res = await api.patch<{ publication: { published: boolean; reason?: string } | null }>(`/admin/standards-watch/events/${id}`, body);
    setPublishMessage(res.publication
      ? res.publication.published
        ? "Published to every organisation enrolled in the standard or carrying the law: it appears on their regulatory watch and My Actions."
        : `Not published to clients: ${res.publication.reason ?? "nothing to publish"}.`
      : "");
    load();
  }

  const refreshActions: RefreshActions = {
    onReview: (refreshId) => setReviewId(refreshId),
    onConvert: async (entryKey, eventId) => {
      await api.post("/admin/standards-watch/refreshes", { entryKey, ...(eventId ? { eventId } : {}) });
      load();
    },
  };
  async function setAutoApply(key: string, enabled: boolean) {
    await api.put(`/admin/standards-watch/entries/${encodeURIComponent(key)}/auto-apply`, { enabled });
    load();
  }
  async function withdrawText(key: string) {
    await api.post(`/admin/standards-watch/entries/${encodeURIComponent(key)}/withdraw-text`, {});
    load();
  }

  // The latest conversion per instrument (the list comes newest first), and per event.
  const refreshFor = useMemo(() => {
    const byEntry = new Map<string, RefreshView>();
    const byEvent = new Map<string, RefreshView>();
    for (const r of data?.refreshes ?? []) {
      if (!byEntry.has(r.entryKey)) byEntry.set(r.entryKey, r);
      if (r.eventId && !byEvent.has(r.eventId)) byEvent.set(r.eventId, r);
    }
    return { byEntry, byEvent };
  }, [data]);
  const entryFor = useMemo(() => new Map((data?.entries ?? []).map((e) => [e.key, e])), [data]);
  const readyRefreshes = useMemo(() => {
    const seen = new Set<string>();
    return (data?.refreshes ?? []).filter((r) => r.status === "ready" && !seen.has(r.entryKey) && seen.add(r.entryKey));
  }, [data]);

  const events = useMemo(() => {
    if (!data) return [];
    if (eventFilter === "open") return data.events.filter((e) => e.status === "open" && e.kind !== "noise");
    if (eventFilter === "noise") return data.events.filter((e) => e.kind === "noise");
    return data.events.filter((e) => e.kind !== "noise");
  }, [data, eventFilter]);

  const entries = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.entries.filter((e) => {
      if (entryFilter === "standard" && e.category !== "standard") return false;
      if (entryFilter === "law" && e.category !== "law") return false;
      if (entryFilter === "unwatched" && e.coverage === "watched") return false;
      if (entryFilter === "failing" && !e.sources.some((s) => s.lastError)) return false;
      if (!q) return true;
      return [e.label, e.implemented.version, ...e.frameworkIds, ...e.regulations, ...e.countries].some((v) => v.toLowerCase().includes(q));
    });
  }, [data, entryFilter, search]);

  if (error) return <div style={{ fontSize: 13, color: theme.color.dangerText }}>{error}</div>;
  if (!data) return <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>;

  const openCount = data.events.filter((e) => e.status === "open" && e.kind !== "noise").length;
  const failingSources = data.entries.reduce((n, e) => n + e.sources.filter((s) => s.lastError).length, 0);
  const run = data.lastRun;
  const filterBtn = (active: boolean): React.CSSProperties => ({
    ...btnSecondary, padding: "5px 10px", fontSize: 12,
    color: active ? theme.color.accentText : theme.color.muted,
    borderColor: active ? theme.color.accent : theme.color.border,
    background: active ? theme.color.accentBg : "transparent",
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Standards & Regulation Watch</div>
          <div style={{ fontSize: 13, color: theme.color.muted, lineHeight: 1.5, maxWidth: 760 }}>
            Every standard and law the platform assesses against, the edition it encodes, and the official source the worker reads daily to learn that it moved.
            A change is classified and, unless it is noise, emailed to the lead consultants of the owner consultancy. Acknowledge what you have seen; mark it applied once the seed, reference text or clocks encode the new edition.
            Where a law's official text is openly licensed, the worker converts it to markdown for you to review and apply as a refresh; purchased standards are flagged only.
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button disabled={downloading} onClick={downloadReport} style={{ ...btnSecondary, padding: "8px 16px", fontSize: 12 }} title="A Word document listing every change to review, the newer editions already known, the unwatched entries and the failing sources, with links to the new versions">
              {downloading ? "Building…" : "Download Word report"}
            </button>
            <button disabled={running} onClick={runNow} style={{ ...btnPrimary, padding: "8px 16px", fontSize: 12 }}>{running ? "Queuing…" : "Check now"}</button>
          </div>
          <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 6 }}>
            {run ? `Last run ${fmtDate(run.startedAt, true)}${run.finishedAt ? "" : " (running)"} · ${run.sourcesChecked} sources, ${run.sourcesChanged} moved, ${run.sourcesFailed} failed, ${run.materialEvents} to review` : "No run yet — the worker runs daily at 05:30 UTC"}
            {run?.error && <div style={{ color: theme.color.dangerText }}>{run.error}</div>}
          </div>
          {runMessage && <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 4, maxWidth: 320 }}>{runMessage}</div>}
          {downloadMessage && <div style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 4, maxWidth: 320 }}>{downloadMessage}</div>}
          {publishMessage && <div role="status" style={{ fontSize: 11, color: theme.color.muted, marginTop: 4, maxWidth: 320 }}>{publishMessage}</div>}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        {[
          { value: openCount, label: "To review", color: openCount ? theme.color.dangerText : theme.color.successText },
          { value: data.entries.filter((e) => (e.implemented.behind?.length ?? 0) > 0).length, label: "Newer editions known", color: theme.color.warningText },
          { value: data.coverage.watched, label: "Watched directly", color: theme.color.successText },
          { value: data.coverage.covered, label: "Covered by another entry", color: theme.color.accentText },
          { value: data.coverage.unwatched, label: "Not watched", color: theme.color.warningText },
          { value: failingSources, label: "Sources failing", color: failingSources ? theme.color.dangerText : theme.color.successText },
        ].map((t) => (
          <div key={t.label} style={{ ...card, padding: "12px 16px", minWidth: 150, flex: 1 }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: t.color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{t.value}</div>
            <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 4, fontWeight: 600 }}>{t.label}</div>
          </div>
        ))}
      </div>

      {readyRefreshes.length > 0 && (
        <div style={{ ...card, padding: "14px 18px", marginBottom: 24, borderColor: theme.color.accent }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>I have converted these to markdown, refresh?</div>
          <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 10, lineHeight: 1.5 }}>
            The official text of each instrument below moved and has been fetched from its catalogue source and converted. Review what changed and the proposed changes to the seeded obligations, then apply the refresh: the compliance check and policy generation read the applied text from then on.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {readyRefreshes.map((r) => (
              <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: 13 }}>
                <span><strong>{r.entryLabel}</strong> <span style={{ color: theme.color.muted, fontSize: 12 }}>converted {fmtDate(r.convertedAt, true)}{r.charCount ? ` · ${r.charCount.toLocaleString("en-GB")} characters` : ""}</span></span>
                <button onClick={() => setReviewId(r.id)} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12 }}>Review</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginRight: 8 }}>Events</div>
        <button style={filterBtn(eventFilter === "open")} onClick={() => setEventFilter("open")}>Open ({openCount})</button>
        <button style={filterBtn(eventFilter === "all")} onClick={() => setEventFilter("all")}>All</button>
        <button style={filterBtn(eventFilter === "noise")} onClick={() => setEventFilter("noise")}>Noise</button>
      </div>
      {events.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "16px 20px", fontSize: 13, color: theme.color.muted, marginBottom: 28 }}>
          {eventFilter === "open" ? "Nothing to review. The first run records what every source says today; changes are raised from the second run on." : "No events."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
          {events.map((e) => (
            <EventCard key={e.id} event={e} onChange={updateEvent} entry={entryFor.get(e.entryKey)} actions={refreshActions}
              refresh={refreshFor.byEvent.get(e.id) ?? (["converting", "ready"].includes(refreshFor.byEntry.get(e.entryKey)?.status ?? "") ? refreshFor.byEntry.get(e.entryKey)! : null)} />
          ))}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginRight: 8 }}>Catalogue ({entries.length})</div>
        <button style={filterBtn(entryFilter === "all")} onClick={() => setEntryFilter("all")}>All</button>
        <button style={filterBtn(entryFilter === "standard")} onClick={() => setEntryFilter("standard")}>Standards</button>
        <button style={filterBtn(entryFilter === "law")} onClick={() => setEntryFilter("law")}>Laws</button>
        <button style={filterBtn(entryFilter === "unwatched")} onClick={() => setEntryFilter("unwatched")}>Not watched directly</button>
        <button style={filterBtn(entryFilter === "failing")} onClick={() => setEntryFilter("failing")}>Failing</button>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" style={{ ...inputStyle, width: 220, padding: "6px 10px", fontSize: 12, marginLeft: "auto" }} />
      </div>
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 2fr) minmax(220px, 3fr) 120px 110px 80px", gap: 12, padding: "8px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: theme.color.muted, borderBottom: "1px solid var(--c-line)", background: theme.color.panelAlt }}>
          <div>Instrument</div><div>Platform encodes</div><div>Coverage</div><div>Last read</div><div style={{ textAlign: "right" }}>Open</div>
        </div>
        {entries.map((e) => (
          <EntryRow key={e.key} entry={e} refresh={refreshFor.byEntry.get(e.key) ?? null} actions={refreshActions} onAutoApply={setAutoApply} onWithdraw={withdrawText} />
        ))}
      </div>

      {data.runs.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>Recent runs</div>
          <div style={{ ...card, padding: "4px 12px", fontSize: 12, color: theme.color.muted }}>
            {data.runs.map((r) => (
              <div key={r.id} style={{ display: "flex", gap: 14, flexWrap: "wrap", padding: "6px 0", borderBottom: `1px solid ${theme.color.border}` }}>
                <span style={{ color: theme.color.text, minWidth: 150 }}>{fmtDate(r.startedAt, true)}</span>
                <span>{r.triggeredBy}</span>
                <span>{r.sourcesChecked} checked</span>
                <span>{r.sourcesChanged} moved</span>
                <span style={{ color: r.sourcesFailed ? theme.color.dangerText : undefined }}>{r.sourcesFailed} failed</span>
                <span>{r.eventsCreated} events ({r.materialEvents} material)</span>
                <span>{r.notified} notified</span>
                {!r.finishedAt && <span style={{ color: theme.color.accentText }}>running</span>}
                {r.error && <span style={{ color: theme.color.dangerText }}>{r.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {reviewId && <StandardsRefreshReview refreshId={reviewId} onClose={() => setReviewId(null)} onChanged={load} />}
    </div>
  );
}
